import { computed, nextTick, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  addTagAlias,
  addTagImplication,
  ApiError,
  createTag,
  deleteTag,
  getMigrationJob,
  listTags,
  removeTagAlias,
  removeTagImplication,
  updateTag,
  type RenameMode,
  type Tag,
  type TagTextFlags,
} from '@/api'
import { setTags } from '@/composables/useTags'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useTodosStore } from '@/stores/todos'
import { aliasCandidates } from '@/todos/tags'

// The state and the actions of the tag settings (the list and the pages of one tag), shared by all of them: the tags are
// loaded once and every page works on the same list, so going from the list to a tag needs no new request.
const tags = ref<Tag[]>([])
const loaded = ref(false)
const loading = ref(false)
const error = ref('')
const saving = ref(false)
const renaming = ref<{ tag: Tag; name: string } | null>(null)
const renameMode = ref<RenameMode>('alias')
const renameProgress = ref<{ done: number; total: number } | null>(null) // (while the todos are rewritten)
const marked = ref<number | null>(null) // (the row a link or a name clash pointed at, marked for a moment)
const drafts = reactive<Record<number, string>>({}) // (typed names, by tag id; `NEW_TAG` is the tag being added)
const aliasDrafts = reactive<Record<number, string>>({})
const impliesDrafts = reactive<Record<number, string>>({})
const todosLoading = ref(false)
const todosRequested = ref(false)

export const NEW_TAG = 0 // (the id of the row being added, which has no tag yet)

export const MIGRATION_POLL_MS = 700

export function cleanName(name: string | undefined): string {
  return (name ?? '').trim().replace(/^#+/, '')
}

export function useTagEditor() {
  const { t } = useI18n()
  const toast = useToast()
  const store = useTodosStore()

  const sorted = computed(() => [...tags.value].sort((a, b) => a.name.localeCompare(b.name)))
  // Tags used on todos that could become an alias. Reading the todos is a live read of every calendar, so it only
  // happens once the first dropdown is used.
  const candidates = computed(() => aliasCandidates(store.knownTags, tags.value))

  function messageOf(e: unknown, fallback: string): string {
    return e instanceof ApiError ? e.message : fallback
  }

  function replace(tag: Tag): void {
    tags.value = tags.value.some((existing) => existing.id === tag.id)
      ? tags.value.map((existing) => (existing.id === tag.id ? tag : existing))
      : [...tags.value, tag]
    setTags(tags.value)
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = ''
    try {
      tags.value = await listTags()
      setTags(tags.value)
      loaded.value = true
    } catch (e) {
      error.value = messageOf(e, t('settings.tags.err.load'))
    } finally {
      loading.value = false
    }
  }

  async function ensureTodosLoaded(): Promise<void> {
    if (todosRequested.value) return
    todosRequested.value = true
    todosLoading.value = true
    try {
      if (!store.calendarsLoaded) await store.loadCalendars()
      await Promise.all(store.calendars.map((calendar) => store.loadTodos(calendar.id)))
    } catch (e) {
      todosRequested.value = false
      error.value = messageOf(e, t('settings.tags.err.load'))
    } finally {
      todosLoading.value = false
    }
  }

  // Marks a tag's row for a moment, scrolled into view (the tags are sorted by name, so a new one may be out of sight).
  async function scrollToTag(id: number): Promise<void> {
    await nextTick()
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    document
      .querySelector(`[data-tag-id="${id}"]`)
      ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' })
  }

  async function markTag(id: number, ms = 2500): Promise<void> {
    await scrollToTag(id)
    marked.value = id
    setTimeout(() => {
      if (marked.value === id) marked.value = null
    }, ms)
  }

  async function run(action: () => Promise<void>, done: () => void = () => {}): Promise<void> {
    saving.value = true
    error.value = ''
    try {
      await action()
      done()
    } catch (e) {
      reportError(e, 'settings.tags.err.save')
      toast.error(e, t('settings.tags.err.save'))
      // (a name that clashes with a tag or alias points at the tag that has it)
      if (e instanceof ApiError && e.conflictTagId) void markTag(e.conflictTagId, 1000)
    } finally {
      saving.value = false
    }
  }

  function cancel(id: number): void {
    delete drafts[id]
  }

  // Creates the tag typed (or picked) as the draft of `NEW_TAG`; `created` gets it, to scroll to or open it.
  async function create(created: (tag: Tag) => void): Promise<void> {
    const name = cleanName(drafts[NEW_TAG])
    if (!name || saving.value) return
    let tag: Tag | null = null
    await run(
      async () => replace((tag = await createTag(name))),
      () => {
        cancel(NEW_TAG)
        if (tag) created(tag)
      },
    )
  }

  // Asks what to do with the todos of the old name; `confirmRename` does it.
  function askRename(tag: Tag): void {
    const name = cleanName(drafts[tag.id])
    if (!name || saving.value) return
    if (name === tag.name) return cancel(tag.id)
    renameMode.value = 'alias'
    renaming.value = { tag, name }
  }

  // The todos of a `migrate` rename are rewritten in the background: ask how far it is until it is over.
  async function waitForMigration(jobId: string): Promise<number> {
    renameProgress.value = { done: 0, total: 0 }
    try {
      for (;;) {
        const job = await getMigrationJob(jobId)
        renameProgress.value = { done: job.done, total: job.total }
        if (job.state === 'done') return job.migrated
        if (job.state === 'failed') throw new Error(job.error ?? 'The rename failed')
        await new Promise((resolve) => setTimeout(resolve, MIGRATION_POLL_MS))
      }
    } finally {
      renameProgress.value = null
    }
  }

  async function confirmRename(): Promise<void> {
    const pending = renaming.value
    if (!pending || saving.value) return
    await run(
      async () => {
        const done = await updateTag(pending.tag.id, {
          name: pending.name,
          rename_mode: renameMode.value,
        })
        replace(done) // (the tag has its new name already, the todos follow)
        const migrated = done.migration_job_id
          ? await waitForMigration(done.migration_job_id)
          : done.migrated_todos
        if (renameMode.value === 'migrate') toast.info(t('settings.tags.migrated', migrated))
      },
      () => {
        renaming.value = null
        cancel(pending.tag.id)
      },
    )
  }

  async function setColor(tag: Tag, color: string | null): Promise<void> {
    await run(async () => replace(await updateTag(tag.id, { color })))
  }

  async function setFlag(
    tag: Tag,
    flag: 'is_category' | 'is_additional' | 'ignore_for_grouping' | 'keep_last' | keyof TagTextFlags,
    value: boolean,
  ): Promise<void> {
    await run(async () => replace(await updateTag(tag.id, { [flag]: value })))
  }

  async function remove(tag: Tag, done: () => void = () => {}): Promise<void> {
    await run(async () => {
      await deleteTag(tag.id)
      tags.value = tags.value.filter((existing) => existing.id !== tag.id)
      setTags(tags.value)
    }, done)
  }

  async function addAlias(tag: Tag): Promise<void> {
    const alias = cleanName(aliasDrafts[tag.id])
    if (!alias) return
    await run(
      async () => replace(await addTagAlias(tag.id, alias)),
      () => delete aliasDrafts[tag.id],
    )
  }

  async function removeAlias(tag: Tag, aliasId: number): Promise<void> {
    await run(async () => replace(await removeTagAlias(tag.id, aliasId)))
  }

  // The tags a tag could still add: the user's own, not itself, not the built-in one, not added yet.
  function impliable(tag: Tag): string[] {
    const added = new Set(tag.implies.map((implied) => implied.tag_id))
    return sorted.value
      .filter((other) => other.id !== tag.id && !other.internal && !added.has(other.id))
      .map((other) => other.name)
  }

  async function addImplication(tag: Tag): Promise<void> {
    const name = cleanName(impliesDrafts[tag.id])
    if (!name) return
    await run(
      async () => {
        await addTagImplication(tag.id, name)
        // (a name that was no tag yet became one, so the whole list is read again)
        tags.value = await listTags()
        setTags(tags.value)
      },
      () => delete impliesDrafts[tag.id],
    )
  }

  async function removeImplication(tag: Tag, impliedTagId: number): Promise<void> {
    await run(async () => replace(await removeTagImplication(tag.id, impliedTagId)))
  }

  return {
    tags,
    sorted,
    loaded,
    loading,
    error,
    saving,
    renaming,
    renameMode,
    renameProgress,
    marked,
    drafts,
    aliasDrafts,
    impliesDrafts,
    candidates,
    todosLoading,
    todosRequested,
    load,
    ensureTodosLoaded,
    scrollToTag,
    markTag,
    cancel,
    create,
    askRename,
    confirmRename,
    setColor,
    setFlag,
    remove,
    addAlias,
    removeAlias,
    impliable,
    addImplication,
    removeImplication,
  }
}
